import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import closeIcon from "../../../../../assets/icons/close-icon.svg";
import { LeadFilterOptionsProps } from "../../../lead-filter/lead-filter.props";
import "../../lead-assignment.scss";
import CreatedTimeFilter from "../../../lead-filter/lead-filter-options/created-time-filter";
import dayjs, { Dayjs } from "dayjs";
import AssignStatusFilter from "./assign-status-filter/assign-status-filter";
import AddressFilter from "./address-filter/address-filter";

const AssignFilterOptions: React.FC<LeadFilterOptionsProps> = (props: any) => {
  const {
    setSelectedLeadsFilters,
    setIsOpenFilter,
    isOpenFilter,
    selectedLeadsFilterOptions,
    setSelectedLeadsFilterOptions,
    onApplyFilters,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    activeFilters,
  } = props;
  const [createdTime, setCreatedTime] = useState<string[]>([]);
  const [createdTimeError, setCreatedTimeError] = useState("");
  const [createdTimeStart, setCreatedTimeStart] = useState<Dayjs | null>(null);
  const [createdTimeEnd, setCreatedTimeEnd] = useState<Dayjs | null>(null);
  const [createdTimeStartError, setCreatedTimeStartError] = useState("");
  const [createdTimeEndError, setCreatedTimeEndError] = useState("");
  const [createdByUserIds, setCreatedByUserIds] = useState<number[]>([]);
  const [leadOwnerIds, setLeadOwnerIds] = useState<number[]>([]);
  const [leadOwnerIdsError, setLeadOwnerIdsError] = useState("");
  const [status, setStatus] = useState<string[]>([]);
  const [statusError, setStatusError] = useState("");
  const [lastRun, setLastRun] = useState<string[]>([]);
  const [lastRunError, setLastRunError] = useState("");
  const [lastRunStart, setLastRunStart] = useState<Dayjs | null>(null);
  const [lastRunEnd, setLastRunEnd] = useState<Dayjs | null>(null);
  const [lastRunStartError, setLastRunStartError] = useState("");
  const [lastRunEndError, setLastRunEndError] = useState("");
  const [address, setAddress] = useState<{country?: string; state?: string; city?: string; postcode?: string}>({
    country: "",
    state: "",
    city: "",
    postcode: "",
  });
  const [addressError, setAddressError] = useState("");
  const [draftSelectedLeadsFilterOptions, setDraftSelectedLeadsFilterOptions] =
    useState<any[]>([]);

  useEffect(() => {
    if (isOpenFilter) {
      // Step 1: Pre-check fields based on activeFilters
      const updatedDraft = selectedLeadsFilterOptions.map((option: any) => {
        switch (option.labels) {
          case "Date Created":
            return {
              ...option,
              value: !!(
                activeFilters?.createdTimeStart && activeFilters?.createdTimeEnd
              ),
            };
          case "Last Run":
            return {
              ...option,
              value: !!(
                activeFilters?.lastRunStart && activeFilters?.lastRunEnd
              ),
            };
          case "Status":
            return {
              ...option,
              value: !!activeFilters?.status?.length,
            };
          case "Address":
            return {
              ...option,
              value: !!activeFilters?.address,
            };
          default:
            return option;
        }
      });

      setDraftSelectedLeadsFilterOptions(updatedDraft);

      if (activeFilters?.createdTimeStart && activeFilters?.createdTimeEnd) {
        setCreatedTime(["Custom"]);
        setCreatedTimeStart(dayjs(activeFilters.createdTimeStart));
        setCreatedTimeEnd(dayjs(activeFilters.createdTimeEnd));
      }

      if (activeFilters?.lastRunStart && activeFilters?.lastRunEnd) {
        setLastRun(["Custom"]);
        setLastRunStart(dayjs(activeFilters.lastRunStart));
        setLastRunEnd(dayjs(activeFilters.lastRunEnd));
      }

      if (activeFilters?.status?.length) {
        setStatus(activeFilters.status);
      }
    }
    if (!isOpenFilter) {
      setStatusError("");
      setCreatedTimeError("");
      setCreatedTimeStartError("");
      setCreatedTimeEndError("");
      setLastRunError("");
      setLastRunStartError("");
      setLastRunEndError("");
      setAddressError("");
     }
  }, [isOpenFilter]);

  const isAnyFilterSelected = draftSelectedLeadsFilterOptions.some(
    (option: any) => option.value
  );
  const handleChange = (e: any) => {
    const updatedDraft = draftSelectedLeadsFilterOptions.map((data: any) =>
      data?.labels === e.target.id ? { ...data, value: !data?.value } : data
    );
    const isLastRunChecked = e.target.id === "Last Run" && e.target.checked;
    const isLastRunUnchecked = e.target.id === "Last Run" && !e.target.checked;

    if (isLastRunChecked) {
      setLastRun(["Custom"]);
    }

    if (isLastRunUnchecked) {
      setLastRun([]);
      setLastRunStart(null);
      setLastRunEnd(null);
      setLastRunError("");
      setLastRunStartError("");
      setLastRunEndError("");
    }

    const isCreatedTimeChecked =
      e.target.id === "Date Created" && e.target.checked;
    const isCreatedTimeUnchecked =
      e.target.id === "Date Created" && !e.target.checked;
    if (isCreatedTimeChecked) {
      // directly assign 'Custom' value
      setCreatedTime(["Custom"]);
    }

    if (isCreatedTimeUnchecked) {
      setCreatedTime([]);
      setCreatedTimeStart(null);
      setCreatedTimeEnd(null);
      setCreatedTimeError("");
      setCreatedTimeStartError("");
      setCreatedTimeEndError("");
    }
    const isStatusChecked = e.target.id === "Status" && e.target.checked;
    const isStatusUnchecked = e.target.id === "Status" && !e.target.checked;

    if (isStatusUnchecked) {
      setStatus([]);
      setStatusError("");
    }

    const isAddressUnchecked = e.target.id === "Address" && !e.target.checked;

    if (isAddressUnchecked) {
      setAddress({country: "", state: "", city: "", postcode: ""});
      setAddressError("");
    }
    setDraftSelectedLeadsFilterOptions(updatedDraft);
  };

  const handleReset = () => {
    const resetFilters = selectedLeadsFilterOptions.map((option: any) => ({
      ...option,
      value: false,
    }));

    setSelectedLeadsFilterOptions(resetFilters);
    setDraftSelectedLeadsFilterOptions(resetFilters);

    setCreatedTimeStart(null);
    setCreatedTimeEnd(null);
    setCreatedTimeStartError("");
    setCreatedTimeEndError("");
    setCreatedTime([]);
    setCreatedTimeError("");
    setCreatedByUserIds([]);
    setLeadOwnerIds([]);
    setLeadOwnerIdsError("");
    setStatus([]);
    setStatusError("");
    setLastRun([]);
    setLastRunStart(null);
    setLastRunEnd(null);
    setLastRunError("");
    setLastRunStartError("");
    setLastRunEndError("");
    setSelectedLeadsFilters("");
    setAddress({country: "", state: "", city: "", postcode: ""});
    setAddressError("");
    // Inform parent component about reset

    props.onApplyFilters({});
    setSelectedLeadsFilters("");
  };

  const handleCreatedTimeDateRange = (from: Date | null, to: Date | null) => {
    setCreatedTimeStart(from ? dayjs(from) : null);
    setCreatedTimeEnd(to ? dayjs(to) : null);
    if (from) setCreatedTimeStartError("");
    if (to) setCreatedTimeEndError("");
  };

  const isCreatedTimeEnabled = draftSelectedLeadsFilterOptions.find(
    (opt: any) => opt.labels === "Date Created"
  )?.value;
  const isStatusEnabled = draftSelectedLeadsFilterOptions.find(
    (opt: any) => opt.labels === "Status"
  )?.value;

  const renderFilterField = (optionLabel: string) => {
    switch (optionLabel) {
      case "Date Created":
        return (
          <CreatedTimeFilter
            value={"Custom"} // hardcoded as "Custom"
            onChange={() => {}} // no need for a dropdown change
            isCreatedTimeEnabled={isCreatedTimeEnabled}
            onDateRangeChange={handleCreatedTimeDateRange}
            createdTimeError={createdTimeError}
            createdTimeStartError={createdTimeStartError}
            createdTimeEndError={createdTimeEndError}
            setCreatedTimeFromDate={setCreatedTimeStart}
            setCretedTimeToDate={setCreatedTimeEnd}
            showDropdown={false}
            fromDate={createdTimeStart}
            toDate={createdTimeEnd}
          />
        );

      case "Last Run":
        return (
          <CreatedTimeFilter
            value={"Custom"}
            onChange={() => {}}
            isCreatedTimeEnabled={
              draftSelectedLeadsFilterOptions.find(
                (opt: any) => opt.labels === "Last Run"
              )?.value
            }
            onDateRangeChange={(from, to) => {
              setLastRunStart(from ? dayjs(from) : null);
              setLastRunEnd(to ? dayjs(to) : null);
              if (from) setLastRunStartError("");
              if (to) setLastRunEndError("");
            }}
            createdTimeError={lastRunError}
            createdTimeStartError={lastRunStartError}
            createdTimeEndError={lastRunEndError}
            setCreatedTimeFromDate={setLastRunStart}
            setCretedTimeToDate={setLastRunEnd}
            showDropdown={false}
            fromDate={lastRunStart}
            toDate={lastRunEnd}
          />
        );
      case "Status":
        return (
          <AssignStatusFilter
            value={status}
            onChange={setStatus}
            isStatusEnabled={
              draftSelectedLeadsFilterOptions.find(
                (opt: any) => opt.labels === "Status"
              )?.value
            }
            error={statusError}
          />
        );
      case "Address":
        return (
          <AddressFilter
            values={address}
            setAddress={setAddress}
            addressError={addressError}
            setAddressError={setAddressError}
            />)
      default:
        return null;
    }
  };
  const handleApplyFilters = () => {
    let hasError = false;

    const isLastRunEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "Last Run"
    )?.value;

    const isCreatedTimeEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "Date Created"
    )?.value;

    const isStatusEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "Status"
    )?.value;

    const isAddressEnabled = draftSelectedLeadsFilterOptions.find(
      (opt: any) => opt.labels === "Address"
    )?.value;

    const filterData: any = {};

    // ---------- LAST RUN ----------
    if (isLastRunEnabled) {
      if (lastRun.length === 0) {
        setLastRunError("Select Last Run time");
        hasError = true;
      } else {
        setLastRunError("");
      }

      if (lastRun.includes("Custom")) {
        if (!lastRunStart) {
          setLastRunStartError("Start date is required");
          hasError = true;
        } else if (
          !dayjs(lastRunStart).isValid() ||
          dayjs(lastRunStart).year() < 1900
        ) {
          setLastRunStartError("Invalid date");
          hasError = true;
        } else {
          setLastRunStartError("");
        }

        if (!lastRunEnd) {
          setLastRunEndError("End date is required");
          hasError = true;
        } else if (
          !dayjs(lastRunEnd).isValid() ||
          dayjs(lastRunEnd).year() < 1900
        ) {
          setLastRunEndError("Invalid date");
          hasError = true;
        } else {
          setLastRunEndError("");
        }

        if (
          lastRunStart &&
          lastRunEnd &&
          dayjs(lastRunStart).isAfter(lastRunEnd)
        ) {
          setLastRunStartError("Start date cannot be after end date");
          setLastRunEndError("End date cannot be before start date");
          hasError = true;
        }

        if (!hasError) {
          filterData.lastRunStart = lastRunStart;
          filterData.lastRunEnd = lastRunEnd;
        }
      }
    }

    // ---------- DATE CREATED ----------
    if (isCreatedTimeEnabled) {
      if (createdTime.length === 0) {
        setCreatedTimeError("Select Date Created");
        hasError = true;
      } else {
        setCreatedTimeError("");
      }

      if (createdTime.includes("Custom")) {
        if (!createdTimeStart) {
          setCreatedTimeStartError("Start date is required");
          hasError = true;
        } else if (
          !dayjs(createdTimeStart).isValid() ||
          dayjs(createdTimeStart).year() < 1900
        ) {
          setCreatedTimeStartError("Invalid date");
          hasError = true;
        } else {
          setCreatedTimeStartError("");
        }

        if (!createdTimeEnd) {
          setCreatedTimeEndError("End date is required");
          hasError = true;
        } else if (
          !dayjs(createdTimeEnd).isValid() ||
          dayjs(createdTimeEnd).year() < 1900
        ) {
          setCreatedTimeEndError("Invalid date");
          hasError = true;
        } else {
          setCreatedTimeEndError("");
        }

        if (
          createdTimeStart &&
          createdTimeEnd &&
          dayjs(createdTimeStart).isValid() &&
          dayjs(createdTimeEnd).isValid() &&
          dayjs(createdTimeStart).isAfter(dayjs(createdTimeEnd))
        ) {
          setCreatedTimeStartError(
            "Start date cannot be greater than end date"
          );
          setCreatedTimeEndError("End date cannot be less than start date");
          hasError = true;
        }

        if (!hasError) {
          filterData.createdTimeStart = createdTimeStart;
          filterData.createdTimeEnd = createdTimeEnd;
        }
      }
    }
    if (isStatusEnabled) {
      if (status.length === 0) {
        setStatusError("Status cannot be empty");
        hasError = true;
      } else {
        setStatusError("");
        filterData.status = status;
      }
    }

    if (isAddressEnabled) {
      console.log('Address filter is enabled with values:', address);
      const { country, state, city, postcode } = address;
      if (!country && !state && !city && !postcode) {
        console.log('no value is there')
        setAddressError("At least one address field must be filled");
        hasError = true;
      } else {
        setAddressError("");
        filterData.address = address;
      }
    }

    // ---------- EXIT IF ERRORS ----------
    if (hasError) return;

    // ---------- APPLY FILTERS ----------
    onApplyFilters(filterData);
    setSelectedLeadsFilterOptions(draftSelectedLeadsFilterOptions);
    setSelectedLeadsFilters(
      draftSelectedLeadsFilterOptions.filter((data: any) => data.value)
    );
    setIsOpenFilter(false); // Close the popup
  };

  return (
    <div className="lead-filter-options-wrapper">
      <div className="lead-filter-options-container">
        <div className="header-wrapper">
          <div className="lead-filter-options-container-header">
            <div className="lead-filter-options-container-header-text">
              Filters
            </div>
            <div className="lead-filter-options-container-header-icon">
              <img
                src={closeIcon}
                alt="Filter Icon"
                onClick={() => {
                  setIsOpenFilter(false);
                }}
              />
            </div>
          </div>
          <div className="lead-filter-options-container-subtext">
            Filter By Fields
          </div>
        </div>
        <div className="lead-filter-options-container-list assignment-rules-filter-options-container">
          {draftSelectedLeadsFilterOptions.map((option: any) => (
            <div>
              <label
                htmlFor={option?.labels}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  ...(option?.value && { height: "10px", marginTop: "30px" }),
                }}
              >
                <Checkbox
                  checked={option?.value}
                  onChange={handleChange}
                  id={option?.labels}
                />
                <ListItemText primary={option?.labels} />
              </label>

              {option?.value && renderFilterField(option?.labels)}
            </div>
          ))}
          {isAnyFilterSelected && (
            <div className="lead-filter-buttons">
              <button
                className="cancel-button"
                onClick={() => {
                  handleReset();
                }}
              >
                Reset
              </button>
              <button
                className="next-button-wrapper"
                type="submit"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignFilterOptions;
